import { CSSProperties } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import type { WorkspaceSummary } from '../../../lib/workspaces';
import { Button } from '../ui';
import { WorkspaceBadge } from '../WorkspaceBadge';
import styles from './BriefSlot.module.css';

// "A new workspace created for you" — the slot on the first morning, when the brief routine is on but
// hasn't run yet. The sentence is the headline; the workspace badge + name sit beneath, then ONE hug
// shortcut — Growth, with a trailing ↗. Figma node 2057:10435 drew two (Agent Work + Growth) from the
// days a Space had two tabs; "Agent Work" was folded into the Dashboard tab, so both buttons opened the
// same page and the second one only asked the reader to pick between identical doors. Mirrors the
// native `BriefNewWorkspaceCard`.

// Workspace name — Archivo SemiBold 20/24 (Figma Display 04), beside the badge.
const NAME_TEXT: CSSProperties = {
  fontFamily: 'var(--brand-font-display)',
  fontSize: 20,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '24px',
  letterSpacing: '-0.4px',
  color: 'rgb(var(--ink-rgb))',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export function NewWorkspaceBrief({ workspace }: { workspace?: WorkspaceSummary }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  if (!workspace) return null;

  const path = `/workspace/${encodeURIComponent(workspace.slug)}`;
  const click = (button_name: string) => () => logPageClickBi({ page_name: 'home', button_name });

  return (
    <section className={styles.slot} aria-labelledby="brief-new-workspace-title">
      <h2 id="brief-new-workspace-title" className={`${styles.title} ${styles.titleFirst}`}>
        {t('brief.newWorkspace.title')}
      </h2>
      <div className={styles.workspace}>
        {/* Figma 2057:10435: a 20px workspace avatar beside the name at Display 04 (20/24). */}
        <WorkspaceBadge ws={workspace} size={20} />
        <span style={NAME_TEXT}>{workspace.name}</span>
      </div>
      <div className={styles.actions}>
        <Button
          href={path}
          endIcon={<ArrowUpRight size={16} className="dir-flip" aria-hidden />}
          onClick={click('brief_new_workspace_growth')}
          data-card-primary
        >
          {t('brief.newWorkspace.growth')}
        </Button>
      </div>
    </section>
  );
}
